import { ChevronDown, ChevronLeft, ChevronRight, Search } from "lucide-react";
import React, { useContext, useEffect, useState } from "react";
import { useDebouncedValue } from "@mantine/hooks";
import {
  ActionIcon,
  Anchor,
  Breadcrumbs,
  Center,
  Group,
  Loader,
  Select,
  Space,
  Text,
  TextInput,
  useMantineTheme,
} from "@mantine/core";
import { AuthContext } from "../../../providers/AuthProvider";
import Segment from "../services/model/Segment";
import useSegmentEmployeesApiService from "../services/api/SegmentEmployeesApiService";
import DirectoryTable from "../../../themes/components/DirectoryTable";
import ResponsiveHeight from "../../../utils/ResponsiveHeight";
import ErrorPage from "../../../common/pages/ErrorPage";

type SegmentEmployeesContentProps = {
  segment: Segment;
  onBack: () => void;
};

const SegmentEmployeesContent: React.FC<SegmentEmployeesContentProps> = ({
  segment,
  onBack,
}) => {
  const auth = useContext(AuthContext);
  const theme = useMantineTheme();

  const tableMaxHeight = ResponsiveHeight(
    "",
    "70vh",
    "70vh",
    "70vh",
    "56vh",
    "70vh"
  );

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchInput, 500);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState("10");

  const pageSize = parseInt(rowsPerPage, 10);

  const { isFetching, isError, error, data } = useSegmentEmployeesApiService(
    {
      segmentId: segment.id,
      search: debouncedSearch,
      page,
      page_size: pageSize,
    },
    Boolean(auth?.employer?.id)
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const totalCount = data?.count ?? 0;
  const totalPages = data?.pages ?? 1;
  const startItem = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalCount);

  const breadcrumbItems = [
    { id: "segmentation", title: "Segmentation", onClick: onBack },
    { id: "segment-name", title: segment.name, onClick: onBack },
    {
      id: "employees",
      title: `Employees (${totalCount})`,
      onClick: undefined,
    },
  ].map((item, index, arr) => (
    <Anchor
      key={item.id}
      onClick={(e) => {
        e.preventDefault();
        if (item.onClick) item.onClick();
      }}
      size="sm"
      style={{
        color: theme.black,
        opacity: index === arr.length - 1 ? 1 : 0.6,
        cursor: index === arr.length - 1 ? "default" : "pointer",
        textDecoration: "none",
      }}
    >
      {item.title}
    </Anchor>
  ));

  return (
    <>
      <Space h="md" />
      <Breadcrumbs separator=">">{breadcrumbItems}</Breadcrumbs>
      <Space h="xl" />

      <TextInput
        icon={<Search size={16} />}
        placeholder="Search employee"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        radius="sm"
        style={{ maxWidth: 400 }}
        styles={{ input: { borderColor: "#cdd3d6" } }}
      />
      <Space h="md" />

      {isFetching && (
        <Center py="xl">
          <Loader size="md" />
        </Center>
      )}

      {isError && <ErrorPage errors={error} />}

      {!isFetching && !isError && data && (
        <>
          {data.results.length === 0 ? (
            <Text color="dimmed" size="sm" align="center" py="xl">
              {debouncedSearch
                ? "No employees found matching your search."
                : "No employees match this segment."}
            </Text>
          ) : (
            <DirectoryTable
              tableMaxHeight={tableMaxHeight}
              data={data.results}
              selection={[]}
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              onSelect={() => {}}
              hideSelectControls
              headers={[
                "Surname",
                "First name",
                "Employee Number",
                "Employee ID",
              ]}
              rows={data.results.map((employee) => ({
                lastName: employee.last_name ?? "-",
                firstName: employee.first_name ?? "-",
                employeeNo: employee.employee_no ?? "-",
                idNumber: employee.id_number ?? "-",
              }))}
            />
          )}

          {totalCount > 0 && (
            <>
              <Space h="md" />
              <Group position="right" spacing="md">
                <Group spacing="xs">
                  <Text size="sm">Rows per page:</Text>
                  <Select
                    value={rowsPerPage}
                    onChange={(val) => {
                      setRowsPerPage(val || "10");
                      setPage(1);
                    }}
                    data={[
                      { value: "10", label: "10" },
                      { value: "25", label: "25" },
                      { value: "50", label: "50" },
                    ]}
                    size="xs"
                    style={{ width: 70 }}
                    rightSection={<ChevronDown size={12} />}
                  />
                </Group>
                <Text size="sm">
                  {startItem}-{endItem} of {totalCount}
                </Text>
                <Group spacing="md">
                  <ActionIcon
                    size="sm"
                    variant="transparent"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                    style={{ opacity: page === 1 ? 0.4 : 1 }}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={14} />
                  </ActionIcon>
                  <ActionIcon
                    size="sm"
                    variant="transparent"
                    disabled={page >= totalPages}
                    onClick={() => setPage(page + 1)}
                    style={{ opacity: page >= totalPages ? 0.4 : 1 }}
                    aria-label="Next page"
                  >
                    <ChevronRight size={14} />
                  </ActionIcon>
                </Group>
              </Group>
            </>
          )}
        </>
      )}
    </>
  );
};

export default SegmentEmployeesContent;
